import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import { fetchCustomers, toggleCustomerStatus } from '../api'; // 👈 import API functions

const COLORS = {
  forestGreen: '#0F3D2E',
  deepGreen: '#003C3C',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  sand: '#F7F3EA',
  border: '#E4D7BC',
  success: '#2F8F4E',
  danger: '#B54135',
  muted: '#746A5B',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const extractCustomerList = (response) => {
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data?.customers)) return response.data.customers;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.customers)) return response.customers;
  if (Array.isArray(response)) return response;
  return [];
};

const formatCustomerName = (customer) =>
  `${customer.firstName || customer.user?.firstName || ''} ${
    customer.lastName || customer.user?.lastName || ''
  }`.trim() ||
  customer.name ||
  customer.email ||
  'Customer';

const getCustomerDate = (customer) => customer.createdAt || customer.updatedAt;

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetchCustomers({ page: 1, limit: 1000 });
      const rawList = extractCustomerList(response);

      const formatted = rawList.map((cust) => ({
        id: cust.id,
        userId: cust.userId || cust.user?.id,
        name: formatCustomerName(cust),
        email: cust.email || cust.user?.email,
        phone: cust.phone || cust.user?.phone,
        address: cust.address,
        status: cust.isAccountActive ? 'Active' : 'Suspended',
        accountActive: cust.isAccountActive,
        createdAt: cust.createdAt,
        updatedAt: cust.updatedAt,
      })).sort((a, b) => {
        const dateA = new Date(getCustomerDate(a) || 0).getTime();
        const dateB = new Date(getCustomerDate(b) || 0).getTime();
        return dateB - dateA;
      });

      setCustomers(formatted);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setNotice({
        type: 'error',
        text: error.message || 'Could not load customers.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return customers.filter((cust) => {
      if (statusFilter && cust.status !== statusFilter) return false;
      if (!term) return true;

      return [cust.name, cust.email, cust.phone, cust.status, cust.address]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [customers, searchTerm, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedCustomers = filtered.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const performToggle = async (cust) => {
    const nextActive = cust.status !== 'Active';

    try {
      setActionId(`status-${cust.id}`);
      setNotice(null);
      await toggleCustomerStatus(cust.id, nextActive);
      await loadCustomers();
      setNotice({
        type: 'success',
        text: `${cust.name} has been ${nextActive ? 'activated' : 'suspended'}.`,
      });
    } catch (error) {
      console.error('Failed to toggle status:', error);
      setNotice({
        type: 'error',
        text: error.message || 'Could not update customer status.',
      });
    } finally {
      setActionId(null);
      setConfirmAction(null);
    }
  };

  const requestToggle = (cust) => {
    const nextActive = cust.status !== 'Active';

    setConfirmAction({
      title: `${nextActive ? 'Activate' : 'Suspend'} customer`,
      message: `${nextActive ? 'Activate' : 'Suspend'} ${cust.name}? ${
        nextActive
          ? 'They will regain access to their customer account.'
          : 'They will be logged out and blocked from customer actions.'
      }`,
      confirmLabel: nextActive ? 'Activate customer' : 'Suspend customer',
      tone: nextActive ? 'success' : 'danger',
      onConfirm: () => performToggle(cust),
    });
  };

  const viewCustomer = (cust) => {
    setSelectedCustomer(cust);
  };

  if (loading) {
    return (
      <Layout>
        <p>Loading customers...</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        eyebrow="Accounts"
        title="Customers"
        subtitle="Review customer accounts and manage access status."
      />

      <div style={styles.toolbar}>
        <input
          type="search"
          placeholder="Search customers by name, email, phone, status..."
          value={searchTerm}
          onChange={handleSearch}
          style={styles.searchInput}
        />
        <select
          value={pageSize}
          onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(1);
          }}
          style={styles.pageSizeSelect}
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option} per page
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(1);
          }}
          style={styles.pageSizeSelect}
        >
          <option value="">All statuses</option>
          <option value="Active">Active customers</option>
          <option value="Suspended">Suspended customers</option>
        </select>
      </div>

      {notice && (
        <div
          style={{
            ...styles.notice,
            ...(notice.type === 'error' ? styles.noticeError : styles.noticeSuccess),
          }}>
          {notice.text}
        </div>
      )}

      <div style={styles.resultMeta}>
        Showing {filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1}-
        {Math.min(safePage * pageSize, filtered.length)} of {filtered.length}{' '}
        customers
      </div>

      {filtered.length === 0 ? (
        <p>No customers found.</p>
      ) : (
        <>
          <div style={styles.tableShell}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, ...styles.actionsHeader }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCustomers.map((cust, index) => (
                  <tr
                    key={cust.id}
                    style={index % 2 === 0 ? styles.evenRow : styles.oddRow}
                  >
                    <td style={styles.td}>{cust.name}</td>
                    <td style={styles.td}>{cust.email || '-'}</td>
                    <td style={styles.td}>{cust.phone || '-'}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.status,
                          backgroundColor:
                            cust.status === 'Active' ? COLORS.success : COLORS.danger,
                        }}
                      >
                        {cust.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionGroup}>
                        <button
                          style={styles.actionBtn}
                          onClick={() => viewCustomer(cust)}
                        >
                          View
                        </button>
                        <button
                          style={{
                            ...styles.actionBtn,
                            backgroundColor:
                              cust.status === 'Active' ? COLORS.heritageGold : COLORS.success,
                          }}
                          onClick={() => requestToggle(cust)}
                          disabled={actionId === `status-${cust.id}`}
                        >
                          {actionId === `status-${cust.id}`
                            ? 'Saving...'
                            : cust.status === 'Active'
                            ? 'Suspend'
                            : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationBar}>
            <button
              type="button"
              style={styles.pageButton}
              disabled={safePage === 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </button>
            <span style={styles.pageText}>
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              style={styles.pageButton}
              disabled={safePage === totalPages}
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
            >
              Next
            </button>
          </div>
        </>
      )}

      {selectedCustomer && (
        <div style={styles.modalOverlay} onClick={() => setSelectedCustomer(null)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <p style={styles.modalEyebrow}>Customer Profile</p>
                <h3 style={styles.modalTitle}>{selectedCustomer.name}</h3>
              </div>
              <button style={styles.closeButton} onClick={() => setSelectedCustomer(null)}>
                Close
              </button>
            </div>
            <div style={styles.detailGrid}>
              <span>Email</span>
              <strong>{selectedCustomer.email || 'Not provided'}</strong>
              <span>Phone</span>
              <strong>{selectedCustomer.phone || 'Not provided'}</strong>
              <span>Account</span>
              <strong>{selectedCustomer.status}</strong>
              <span>Address</span>
              <strong>{selectedCustomer.address || 'Not provided'}</strong>
              <span>Created</span>
              <strong>
                {selectedCustomer.createdAt
                  ? new Date(selectedCustomer.createdAt).toLocaleDateString()
                  : 'Not provided'}
              </strong>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel}
        tone={confirmAction?.tone}
        loading={!!actionId}
        onCancel={() => setConfirmAction(null)}
        onConfirm={confirmAction?.onConfirm}
      />
    </Layout>
  );
}

const styles = {
  toolbar: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 1fr) 180px 160px',
    gap: 12,
    alignItems: 'center',
    marginBottom: 12,
  },
  searchInput: {
    padding: 10,
    width: '100%',
    fontSize: 16,
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.deepGreen,
    boxSizing: 'border-box',
  },
  pageSizeSelect: {
    padding: 10,
    width: '100%',
    fontSize: 14,
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.deepGreen,
    fontWeight: 800,
  },
  resultMeta: {
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 12,
  },
  tableShell: {
    width: '100%',
    overflowX: 'auto',
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    boxShadow: '0 14px 32px rgba(15, 61, 46, 0.08)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: COLORS.offWhite,
    minWidth: 820,
  },
  thead: {
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    textAlign: 'left',
  },
  th: {
    padding: '15px 18px',
    fontWeight: 'bold',
  },
  td: {
    padding: '16px 18px',
    borderBottom: `1px solid ${COLORS.border}`,
    verticalAlign: 'middle',
    color: COLORS.deepGreen,
  },
  evenRow: {
    backgroundColor: COLORS.offWhite,
  },
  oddRow: {
    backgroundColor: COLORS.sand,
  },
  status: {
    color: '#fff',
    padding: '6px 12px',
    borderRadius: 12,
    fontSize: 12,
    display: 'inline-block',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  actionBtn: {
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    padding: '7px 12px',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    userSelect: 'none',
    lineHeight: 1,
    minHeight: 32,
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(15, 61, 46, 0.12)',
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    minWidth: 160,
  },
  actionsHeader: {
    minWidth: 160,
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '12px 0',
  },
  pageButton: {
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 11px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  pageText: {
    color: COLORS.muted,
    fontWeight: 800,
    fontSize: 13,
  },
  notice: {
    padding: '12px 14px',
    borderRadius: 8,
    marginBottom: 16,
    fontWeight: 800,
  },
  noticeSuccess: {
    backgroundColor: '#E7F6ED',
    color: COLORS.success,
    border: `1px solid ${COLORS.success}`,
  },
  noticeError: {
    backgroundColor: '#FCEAE8',
    color: COLORS.danger,
    border: `1px solid ${COLORS.danger}`,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 61, 46, 0.42)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    zIndex: 1000,
  },
  modal: {
    width: 'min(520px, 100%)',
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: 22,
    boxShadow: '0 24px 80px rgba(15, 61, 46, 0.22)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 18,
  },
  modalEyebrow: {
    margin: 0,
    color: COLORS.heritageGold,
    fontWeight: 900,
    textTransform: 'uppercase',
    fontSize: 12,
  },
  modalTitle: {
    margin: '4px 0 0',
    color: COLORS.forestGreen,
    fontSize: 24,
  },
  closeButton: {
    alignSelf: 'flex-start',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '8px 12px',
    fontWeight: 800,
    cursor: 'pointer',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    gap: '12px 16px',
    color: COLORS.deepGreen,
  },
};

